<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { format, startOfDay, endOfDay } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { locales } from '@/helpers/date-locales';

const props = defineProps({
  modelValue: {
    type: [Object, String, Array],
    default() {
      return null;
    },
  },
  label: {
    type: String,
    default() {
      return '';
    },
  },
  placeholder: {
    type: String,
    default() {
      return '';
    },
  },
  externalFormat: {
    type: String,
    default() {
      return 'dd MMM yyyy';
    },
  },
  modelType: {
    type: [String, undefined],
    default() {
      return undefined;
    },
  },
  disabled: {
    type: Boolean,
    default() {
      return false;
    },
  },
  isReadonly: {
    type: Boolean,
    default() {
      return false;
    },
  },
  pickerOptions: {
    type: Array,
    default() {
      return [];
    },
  },
  multiCalendars: {
    type: [Object, Boolean],
    default() {
      return {
        solo: true,
      };
    },
  },
  teleport: {
    type: Boolean,
    default() {
      return false;
    },
  },
  clearable: {
    type: Boolean,
    default: true,
  },
  isValueRequired: {
    type: Boolean,
    default: false,
  },
  presetDatesFactory: {
    type: Function,
    default: () => [],
  },
  disabledDates: {
    type: Array,
    default: () => [],
  },
  defaultValue: {
    type: Number,
    default: null,
  },
  range: {
    type: Boolean,
    default: true,
  },
  customInput: {
    type: Boolean,
    default: false,
  },
  alwaysClearable: {
    type: Boolean,
    default: true,
  },
  autoApply: {
    type: Boolean,
    default: false,
  },
  autoPosition: {
    type: [Boolean, String],
    default: 'bottom top left right',
  },
  offset: {
    type: Number,
    default: undefined,
  },
  shortcutName: {
    type: String,
    default: null,
  },
  error: {
    type: [String, Number, Array],
    default() {
      return '';
    },
  },
  isError: {
    type: Boolean,
    default() {
      return false;
    },
  },
  enableTimePicker: {
    type: Boolean,
    default() {
      return false;
    },
  },
  minDate: {
    type: [String, Date],
    default: null,
  },
  maxDate: {
    type: [String, Date],
    default: null,
  },
  startDate: {
    type: [String, Date],
    default: null,
  },
  size: {
    type: String,
    default: '',
  },
  ui: {
    type: Object,
    default: () => ({}),
  },
  config: {
    type: Object,
    default: () => ({}),
  },
});
const emit = defineEmits(['update:modelValue', 'clear']);
const $i18n = useI18n();
import { useError } from '@/composables/useError';
const internalDate = ref();
const datePickerRef = ref();
const currentShortcutName = ref(props.shortcutName);
const isPresetSelection = ref(true);
const readonly = ref(false);
const isReadonly = computed(() => props.isReadonly || readonly.value);
const { currentError } = useError({
  error: computed(() => {
    return props.error;
  }),
});
const startTime = ref({ hours: 0, minutes: 0, seconds: 0 });
const rangeStartTime = [
  { hours: 0, minutes: 0, seconds: 0 },
  { hours: 23, minutes: 59, seconds: 59 },
];

const currentLang = computed(() => {
  return $i18n.locale.value;
});
const isDisableSave = computed(() => {
  return (props.range && internalDate.value && internalDate.value.length < 2)
    || (props.isValueRequired && !internalDate.value);
});
const pConfig = computed(() => {
  const baseConfig = {
    noSwipe: false,
    closeOnScroll: true,
    shadowDom: true,
    allowStopPropagation: false,
    onClickOutside: (validate, evt) => {
      if(validate()) closeDate();
    },
  };

  return {...baseConfig, ...props.config};
});
watch(() => currentLang.value, () => {
  if(datePickerRef.value.formatInputValue) {
    datePickerRef.value.formatInputValue();
  } else {
    datePickerRef.value.selectDate();
  }
});
watch(() => props.shortcutName, (val) => {
  currentShortcutName.value = val;
  isPresetSelection.value = true;
});

const handleInternal = (date) => {
  internalDate.value = date;
};
const handlerRangeStart = () => {
  isPresetSelection.value = false;
};
const handleClear = () => {
  readonly.value = true;
  if(props.defaultValue) {
    const defaultDate = props.presetDatesFactory()[props.defaultValue].value;
    if(props.shortcutName) {
      currentShortcutName.value = defaultDate.short_title;
    }
    emit('update:modelValue', null, currentShortcutName.value);
    nextTick(() => {
      emit('update:modelValue', defaultDate.range, currentShortcutName.value);
    });
  } else {
    emit('update:modelValue', null);
    nextTick(() => {
      emit('update:modelValue', null);
    });
  }
  setTimeout(()=> {
    readonly.value = false;
  }, 0);
};
const formatValue = (date, frmt = 'dd MMM yyyy') => {
  return date ? format(date, frmt, { locale: locales[currentLang.value] }) : '';
};

const customFormat = (date) => {
  if(props.range) {
    if (!date || !Array.isArray(date) || date.length !== 2) {
      return date?.[0] ? formatValue(date[0], props.externalFormat) : '';
    }

    const [start, end] = date;
    const rangeStr = `${formatValue(start, props.externalFormat)} - ${formatValue(end, props.externalFormat)}`;

    const matchedPreset = props.presetDatesFactory()?.find((preset) =>
      preset.value?.short_title && preset.value?.short_title === currentShortcutName.value,
    );

    return matchedPreset ? `${matchedPreset.label} (${rangeStr})` : rangeStr;
  } else {
    if (!date) return '';
    return formatValue(date, props.externalFormat) ;
  }
};

const customPreview = (date) => {
  if(props.range) {
    return `${formatValue(date[0], props.externalFormat)} - ${formatValue(date[1], props.externalFormat)}`;
  } else {
    if (!date) return '';
    return formatValue(date, props.externalFormat) ;
  }
};

const formattedModelValue = computed(() => customFormat(props.modelValue));

const selectDate = () => {
  datePickerRef.value.selectDate();
};
const closeDate = () => {
  currentShortcutName.value = props.shortcutName;
  datePickerRef.value.closeMenu();
};

const handlePresetClick = (value, presetDate) => {
  if(!props.autoApply) {
    isPresetSelection.value = true;
    currentShortcutName.value = value.short_title;
  }
  presetDate(value.range);
};

const onUpdate = (value) => {
  if(props.range) {
    if (isPresetSelection.value) {
      emit('update:modelValue', value, currentShortcutName.value);
      return;
    }
    if (!value || !Array.isArray(value)) return;
    if(props.enableTimePicker) {
      emit('update:modelValue', value, null);
      return;
    }
    const [start, end] = value;
    emit('update:modelValue', [startOfDay(start), endOfDay(end)], null);
  } else {
    emit('update:modelValue', value);
  }
};
</script>

<template>
  <div
    :class="[
      'ui-datepicker',
      {'ui-datepicker_disabled': disabled},
      {'ui-datepicker_readonly': isReadonly},
      {'ui-datepicker_dynamic': customInput},
      {'ui-datepicker_range': range},
      {'ui-datepicker_error': currentError || props.isError },
      `ui-datepicker_${size}`
    ]"
  >
    <UiLabel
      v-if="label"
      :label="label"
      :disabled="disabled"
      class="t:mb-8"
    />
    <VueDatePicker
      ref="datePickerRef"
      :model-value="modelValue"
      :locale="currentLang"
      :format="customFormat"
      :preview-format="customPreview"
      :placeholder="placeholder"
      :disabled="disabled"
      :range="range"
      :readonly="isReadonly"
      :multi-calendars="multiCalendars"
      :clearable="clearable"
      :enable-time-picker="enableTimePicker"
      :teleport="teleport"
      :preset-dates="presetDatesFactory ? presetDatesFactory() : []"
      :month-change-on-scroll="false"
      :always-clearable="alwaysClearable"
      :auto-apply="autoApply"
      :auto-position="autoPosition"
      :offset="offset"
      :model-type="modelType"
      time-picker-inline
      :start-time="range ? rangeStartTime : startTime"
      :disabled-dates="disabledDates"
      :ui="{
        menu: `ui-datepicker__menu ${props.autoApply ? 'ui-datepicker__menu_auto-apply' : ''} ${!props.range ? 'ui-datepicker__menu_custom-preview' : ''} ${props.ui.menu || ''}`,
        calendar: `ui-datepicker__calendar ${props.ui.calendar || ''}`,
        calendarCell: `ui-datepicker__calendar-cell ${props.ui.calendarCell || ''}`,
      }"
      :config="pConfig"
      :start-date="startDate"
      :max-date="maxDate"
      :min-date="minDate"
      @cleared="handleClear"
      @update:model-value="onUpdate"
      @internal-model-change="handleInternal"
      @range-start="handlerRangeStart"
    >
      <template v-slot:input-icon>
        <UiIcon
          color="#7d7d7d"
          name="calendar-days"
        />
      </template>
      <template
        v-if="props.customInput"
        v-slot:dp-input="{ value, isMenuOpen }"
      >
        <div
          :class="[
            'ui-datepicker__input',
            {'ui-datepicker__input_focus': isMenuOpen},
          ]"
        >
          {{ value || formattedModelValue || props.placeholder }}
        </div>
      </template>
      <template v-slot:clear-icon="{ clear }">
        <UiIcon
          :size="14"
          color="#a6b2c4"
          name="times-circle"
          @click="clear"
        />
      </template>

      <template v-slot:preset-date-range-button="{ label, value, presetDate }">
        <div
          :class="[
            {
              'ui-datepicker__preset-range': value.type === 'picker',
              'ui-datepicker__preset-range_active': currentShortcutName === value.short_title,
              'ui-datepicker__title': value.type === 'group-title',
            }
          ]"
          role="button"
          :tabindex="0"
          @click="handlePresetClick(value, presetDate)"
        >
          {{ label }}
        </div>
      </template>
      <template v-slot:action-buttons>
        <UiButton
          type="negative"
          icon="times"
          @click="closeDate"
        >
          {{ $t(`marketingMaterials.popup.cancel`) }}
        </UiButton>
        <UiButton
          class="t:ml-8"
          filled
          icon="check"
          :disabled="isDisableSave"
          @click="selectDate"
        >
          {{ $t(`marketingMaterials.popup.save`) }}
        </UiButton>
      </template>
    </VueDatePicker>
    <UiHelp
      v-if="currentError"
      :is-error="true"
      :text="currentError"
    />
  </div>
</template>

<style lang="scss">
.dp__theme_light{
  --dp-range-between-dates-background-color: #f4f7fa;
  --dp-range-between-border-color: #f4f7fa;
  --dp-range-between-dates-text-color: var(--text-body);
  --dp-hover-text-color: #fff;
  --dp-primary-color: var(--text-primary);
  --dp-font-family: Roboto, Helvetica, Arial, sans-serif;
  --dp-hover-color: var(--text-primary);
  --dp-hover-icon-color: var(--text-primary);
  --dp-primary-disabled-color: var(--text-primary);

}
.ui-datepicker {
  .dp--menu-wrapper {
    z-index: 2001;
  }

  &__menu {
    .dp--tp-wrap{
      max-width: 100%;
      .dp__flex{
        width: 100%;
      }
    }
    .dp--menu-wrapper{
      border-radius: 8px;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, .1);
    }
    .dp--preset-dates{
      width: 200px;
      height: calc(100% - 47px);
      border-bottom: none;
      padding: 0;
      top: 0;
      position: absolute;
      overflow: auto;
    }
    .dp--preset-dates + .dp__instance_calendar{
      margin-left: 200px;
      //padding-bottom: 0;
    }
    .dp__action_row{
      border-top: 1px solid #e4e4e4;
      padding: 8px 16px;
      justify-content: flex-end;
    }
    .dp__action_buttons{
      margin-inline-start: inherit;
      margin-left: 8px;
    }
    .dp__selection_preview{
      text-transform: capitalize;
      font-size: 14px;
      line-height: 1.3;
      color: #303634;
    }
    .dp__calendar_header_item{
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .dp__button:hover,
    .dp__inner_nav:hover{
      background: transparent;
    }
    .dp__range_end.dp__range_start{
      border-radius: 4px;
    }
    .dp__overlay_cell_disabled{
      cursor: default;
      background: none;
      filter: grayscale(1);
      opacity: .5;
      &:hover {
        color: var(--dp-text-color);
      }
    }
    .dp__overlay_cell_active_disabled {
      background: var(--dp-primary-disabled-color);
    }
    .dp__menu_inner{
      padding: 0;
      .dp__calendar_next{
        margin-left: 0;
      }
      .dp__instance_calendar{
        padding: 8px;
      }
      .dp__instance_calendar:last-child{
        border-left: 1px solid #e4e4e4;
      }
    }
    .dp--preset-dates + .dp__instance_calendar{
      margin-left: 200px;
    }

    &_auto-apply{
      .dp--preset-dates {
        height: 100%;
      }
    }
    &_custom-preview {
      .dp__action_row{
        flex-direction: column;
        align-items: flex-end;
      }
      .dp__selection_preview {
        margin-bottom: 8px;
        max-width: 100% !important;
      }
    }
  }
  &__calendar {
    .dp__calendar,
    .dp__calendar_header {
      padding: 0 6px;
    }
  }
  .dp__input_icon{
    cursor: default;
    left: 8px;
  }
  .dp--clear-btn{
    right: 8px;
    .fas{
      padding-right: 0 !important;
    }
  }

  .dp__input_wrap{
    line-height: 0;
  }
  &__title {
    line-height: 1;
    color: #303634;
    display: block;
    padding: 8px 6px 8px 16px;
    margin-top: 10px;
    font-weight: 700;
    font-size: 14px;
    white-space: break-spaces;
    pointer-events: none;
    &:hover {
      background-color: #fff;
    }
  }
  &__preset-range {
    font-size: 14px;
    font-family: var(--main-font);
    padding: 8px 6px 8px 16px;
    line-height: 1.1;
    color: var(--text-body);
    cursor: pointer;

    &_active,
    &:hover {
      color: var(--text-body-invert);
      background-color: var(--text-primary);
    }
  }
  .dp__input,
  &__input {
    font-size: 13px;
    height: 30px;
    color: #212529;
    background-color: white;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    cursor: text;
    width: 100%;
    min-width: 100px;
    box-sizing: border-box;
    position: relative;
    padding: 8px 26px 8px 22px;
    display: inline-flex;
    align-items: center;
    border: 1px solid #d3d3d3;
    border-radius: 5px;
    transition: border-color .2s cubic-bezier(.645,.045,.355,1);
    &_focus {
      border-color: var(--text-primary);
    }
  }
  &_big &{
    &__input {
      padding: 8px 30px 8px 26px;
      height: 34px;
    }
  }
  &_big{
    .dp__input_icon{
      left: 12px;
    }
    .dp--clear-btn{
      right: 12px;
    }
    .dp__input{
      padding: 8px 30px 8px 26px;
      height: 34px;
    }
  }
  .dp__input:hover:not(.dp__input_focus){
    border: 1px solid #d3d3d3;
  }
  &_disabled{
    opacity: .5;
    cursor: default;
  }
  &_readonly{
    cursor: not-allowed;
    .dp__input,
    .ui-datepicker__input {
      pointer-events: none;
    }
  }
  &_dynamic &{
    &__input {
      width: auto;
    }
  }
  .dp__input::placeholder{
    opacity: 0.3;
  }
  &_error {
    .dp__input,
    .ui-datepicker__input {
      border-color: var(--border-negative);
    }
    .dp__input:hover:not(.dp__input_focus){
      border-color: var(--border-negative);
    }
  }
}
</style>
